import { IFeature } from '@principle-theorem/feature-flags';
import { MARKETING_CAMPAIGNS_FEATURE } from '@principle-theorem/principle-core/features';
import {
  ISelectOption,
  ISoftDelete,
  Timestamp,
} from '@principle-theorem/shared';
import { IAddressMetadata } from '../address/address';
import { IMohSchool } from '../transaction/moh/moh';

export type PatientMetadataValue = string | boolean | number | string[];

export type IPatientMetadata = Partial<ICustomMetadata> &
  Partial<ISystemPatientMetadata> &
  Partial<IBridgePatientMetadata> &
  Partial<INzPatientMetadata> &
  Partial<IParchmentPatientMetadata>;

interface ICustomPatientMetadata {
  [key: string]: PatientMetadataValue;
}

export interface ICustomMetadata {
  custom: ICustomPatientMetadata;
}

export interface ISystemPatientMetadata {
  rescheduleReasons: Record<string, PatientMetadataValue>;
  address: IAddressMetadata;
  receiveMarketing: boolean;
}

interface IBridgePatientMetadata {
  shortId: string;
}

export interface INzPatientMetadata {
  moh: {
    school?: IMohSchool;
    enrolmentFormPath?: string;
    enrolmentFormSentAt?: Timestamp;
  };
}

export interface IParchmentPatientMetadata {
  parchment: {
    parchmentPatientId: string;
    url: string;
  };
}

export enum PatientMetadataType {
  String = 'string',
  Boolean = 'boolean',
  Number = 'number',
}

export type IPatientMetadataConfiguration = ISoftDelete & {
  key: string;
  label: string;
  description: string;
  type: PatientMetadataType;
  feature?: IFeature;
  options?: ISelectOption<PatientMetadataValue>[];
} & (
    | { multiple: true; defaultValue?: PatientMetadataValue[] }
    | { multiple: false; defaultValue?: PatientMetadataValue }
  );

export enum PatientMetadataDisplayFormat {
  Label = 'label',
  LabelValue = 'labelValue',
  Value = 'value',
}

export enum PatientMetadataDisplayStyle {
  Text = 'text',
  Chip = 'chip',
}

export enum PatientMetadataDisplayTarget {
  AppointmentSidebarBesidePatientName = 'appointmentSidebarBesidePatientName',
  AppointmentSidebarUnderTags = 'appointmentSidebarUnderTags',
  PatientProfileHeader = 'patientProfileHeader',
  PatientProfilePatientDetailsUnderTags = 'patientProfilePatientDetailsUnderTags',
}

interface IPatientMetadataDisplayTargetInfo {
  label: string;
  description: string;
  imageUrl: string;
}

export const PATIENT_METADATA_DISPLAY_TARGET_INFO: {
  [key in PatientMetadataDisplayTarget]: IPatientMetadataDisplayTargetInfo;
} = {
  [PatientMetadataDisplayTarget.AppointmentSidebarBesidePatientName]: {
    label: `Appointment sidebar - Next to patient's name`,
    description: `Shown in the sidebar next to the patient's name when scheduling, rescheduling, cancelling or during an appointment.`,
    imageUrl: `/assets/help-images/metadata-outlets/appointment-sidebar-beside-patient-name.png`,
  },
  [PatientMetadataDisplayTarget.AppointmentSidebarUnderTags]: {
    label: `Appointment sidebar - Under patient's tags`,
    description: `Shown in the sidebar under a patient's tags when scheduling, rescheduling, cancelling or during an appointment.`,
    imageUrl: `/assets/help-images/metadata-outlets/appointment-sidebar-under-tags.png`,
  },
  [PatientMetadataDisplayTarget.PatientProfileHeader]: {
    label: `Patient profile - Header`,
    description: `Shown in the header of a patient's profile. This is hidden on smaller screens.`,
    imageUrl: `/assets/help-images/metadata-outlets/patient-profile-header.png`,
  },
  [PatientMetadataDisplayTarget.PatientProfilePatientDetailsUnderTags]: {
    label: `Patient profile - Patient details under patient's tags`,
    description: `Shown in the Patient Details card on a patient's profile under the patient's tags.`,
    imageUrl: `/assets/help-images/metadata-outlets/patient-profile-patient-details-under-tags.png`,
  },
};

export interface IPatientMetadataDisplay extends ISoftDelete {
  label: string;
  metadataKey: string;
  format: PatientMetadataDisplayFormat;
  style?: PatientMetadataDisplayStyle;
  hexColour: string;
  displayTargets: PatientMetadataDisplayTarget[];
  onlyShowIfHasValue: boolean;
}

export const CANCELLATION_REASON_METADATA_CONFIGURATION: IPatientMetadataConfiguration =
  {
    key: 'cancellationReason',
    label: 'Cancellation/reschedule reason',
    description: 'The reason the appointment was cancelled/rescheduled.',
    type: PatientMetadataType.Number,
    multiple: false,
    defaultValue: 0,
    deleted: false,
  };

export const RECEIVE_MARKETING_METADATA_KEY = 'receiveMarketing';

export const RECEIVE_MARKETING_KETADATA_CONFIGURATION: IPatientMetadataConfiguration =
  {
    key: RECEIVE_MARKETING_METADATA_KEY,
    label: 'Opt-in to Receive Marketing',
    description:
      'Opting in will allow us to send you marketing emails & promotions.',
    type: PatientMetadataType.Boolean,
    defaultValue: true,
    multiple: false,
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
    deleted: false,
    feature: MARKETING_CAMPAIGNS_FEATURE,
  };

export const SYSTEM_PATIENT_METADATA_DISPLAY: IPatientMetadataDisplay = {
  label: 'Receive Marketing',
  metadataKey: RECEIVE_MARKETING_METADATA_KEY,
  format: PatientMetadataDisplayFormat.LabelValue,
  style: PatientMetadataDisplayStyle.Text,
  hexColour: '#000000',
  displayTargets: [
    PatientMetadataDisplayTarget.PatientProfilePatientDetailsUnderTags,
  ],
  onlyShowIfHasValue: true,
  deleted: false,
};

export const SYSTEM_PATIENT_PROFILE_METADATA_DISPLAYS: IPatientMetadataDisplay[] =
  [SYSTEM_PATIENT_METADATA_DISPLAY];

export const SYSTEM_PATIENT_PROFILE_METADATA_CONFIGURATIONS: IPatientMetadataConfiguration[] =
  [RECEIVE_MARKETING_KETADATA_CONFIGURATION];

export const SYSTEM_METADATA_CONFIGURATIONS: IPatientMetadataConfiguration[] = [
  RECEIVE_MARKETING_KETADATA_CONFIGURATION,
  CANCELLATION_REASON_METADATA_CONFIGURATION,
];
